/**
 * GitLab Pipeline Visualization 组件
 * 在展开区域显示 Pipeline Jobs 的拓扑图
 */

import { FC, useEffect, useState, createContext, useContext } from 'react';
import { CircularProgress, Typography } from '@material-ui/core';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { getGraphDataModel, GitLabPipeline, GitLabJob } from './gitlab-topology-utils';
import { GitLabPipelineLayout } from './GitLabPipelineLayout';
import { GitLabJobLogDialog } from '../GitLabJobLogDialog';

import '@patternfly/react-core/dist/styles/base-no-reset.css';
import '@patternfly/patternfly/utilities/Accessibility/accessibility.css';

// Context 用于在拓扑图节点中打开日志对话框
interface GitLabJobsContextType {
  jobs: GitLabJob[];
  openLogDialog: (jobId: number) => void;
}

export const GitLabJobsContext = createContext<GitLabJobsContextType>({
  jobs: [],
  openLogDialog: () => {},
});

interface GitLabPipelineVisualizationProps {
  pipeline: GitLabPipeline;
  projectPath: string;
  onJobsLoaded?: (count: number, jobsInfo: Array<{stage: string, status: string}>) => void;
}

export const GitLabPipelineVisualization: FC<GitLabPipelineVisualizationProps> = ({
  pipeline,
  projectPath,
  onJobsLoaded,
}) => {
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [jobs, setJobs] = useState<GitLabJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | undefined>();

  useEffect(() => {
    const fetchJobs = async () => {
      setLoadingJobs(true);
      try {
        const backendUrl = await discoveryApi.getBaseUrl('');
        const { token } = await identityApi.getCredentials();
        
        const response = await fetch(
          `${backendUrl}gitlab/rest/git.ekuaibao.com/projects/${encodeURIComponent(projectPath)}/pipelines/${pipeline.id}/jobs`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          const jobsData = data || [];
          // 按创建时间排序 - 从早到晚
          const sortedJobs = jobsData.sort((a: GitLabJob, b: GitLabJob) => {
            return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          });
          setJobs(sortedJobs);
          // 通知父组件 Jobs 数量和信息列表 - 只在这里调用一次
          if (onJobsLoaded) {
            const jobsInfo = sortedJobs.map((job: GitLabJob) => ({
              stage: job.stage,
              status: job.status,
            }));
            onJobsLoaded(sortedJobs.length, jobsInfo);
          }
        }
      } catch (error) {
        setJobs([]);
      } finally {
        setLoadingJobs(false);
      }
    };
    
    fetchJobs();
    // 不包含 onJobsLoaded 在依赖项中，避免无限循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeline.id, projectPath, identityApi]);

  const openLogDialog = (jobId: number) => {
    setSelectedJobId(jobId);
    setLogDialogOpen(true);
  };

  if (loadingJobs) {
    return <CircularProgress size={24} />;
  }

  if (jobs.length === 0) {
    return <Typography variant="body2">暂无 Jobs 信息</Typography>;
  }

  const model = getGraphDataModel(pipeline, jobs);

  if (!model || (model.nodes.length === 0 && model.edges.length === 0)) {
    return <Typography variant="body2">无法生成流程图</Typography>;
  }

  // 根据 jobs 数量动态计算高度
  const calculateHeight = () => {
    const jobCount = jobs.length;
    // 每个 job 大约占 50px 高度，最小 200px，最大 380px
    const calculatedHeight = Math.max(200, Math.min(380, jobCount * 50));
    return `${calculatedHeight}px`;
  };

  return (
    <GitLabJobsContext.Provider value={{ jobs, openLogDialog }}>
      <div
        data-testid="gitlab-pipeline-visualization"
        style={{
          height: calculateHeight(),
          width: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          overflow: 'auto',
        }}
      >
        <GitLabPipelineLayout model={model} />
      </div>
      
      {/* 日志查看对话框 - 复用 Tekton 的布局 */}
      <GitLabJobLogDialog
        open={logDialogOpen}
        onClose={() => setLogDialogOpen(false)}
        jobs={jobs}
        pipelineName={`Pipeline #${pipeline.iid}`}
        projectPath={projectPath}
        initialJobId={selectedJobId}
      />
    </GitLabJobsContext.Provider>
  );
};

export default GitLabPipelineVisualization;

